import { observer } from 'mobx-react-lite'
import { Avatar } from '@/components/Avatar'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromInfiniteScrollProvider } from '@/components/inputs/infinite-scroll-provider-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { FormGroup } from '@/components/Form'
import { type RequestProviderSelectorViewModel } from '@/features/jobs/drafting/RequestProviderSelectorViewModel'
import { RequestedProviderSelection } from '@/features/jobs/drafting/DraftPersister'

export interface CompactRequestProviderSelectorContainerProps {
  vm: RequestProviderSelectorViewModel
}

export const CompactRequestProviderSelectorContainer = observer(
  function CompactRequestProviderSelectorContainer({
    vm,
  }: CompactRequestProviderSelectorContainerProps) {
    const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
      scrollProvider: vm.scroller,
      searchText: vm.searchText.value,
      onSearchTextChange: vm.triggerSearch,
      searchResults: vm.filteredMembers,
    })

    const nullSelectionPlaceholder = vm.nullSelectionPlaceholder

    return (
      <FormGroup>
        <CustomSelect
          {...selectProps}
          placeholder="Select provider"
          itemToString={(v) =>
            v === RequestedProviderSelection.firstAvailable
              ? nullSelectionPlaceholder
              : String(v?.displayName)
          }
          isDisabled={vm.disabled}
          focus$={vm.step.becomeActive$}
          onFocus={vm.step.onBecameActive}
          value={vm.chosenProvider ?? undefined}
          onChange={vm.selectProvider}
          multi={false}
          testId="compact-request-provider-selector"
          renderItem={(itemProps) => {
            const display = getDisplayPropsForProvider(
              itemProps.item,
              nullSelectionPlaceholder,
            )

            return (
              <SingleItemSelectedDefault
                visual={
                  display.avatar && (
                    <Avatar
                      size={24}
                      subject={{
                        displayName: display.name,
                        avatar: display.avatar,
                      }}
                    />
                  )
                }
                {...itemProps}
              />
            )
          }}
          renderSearchItem={({
            item,
            itemToString: _itemToString,
            ...itemProps
          }) => {
            const display = getDisplayPropsForProvider(
              item,
              nullSelectionPlaceholder,
            )

            return (
              <SearchResultItem
                {...itemProps}
                visual={
                  display.avatar && (
                    <Avatar
                      size={24}
                      subject={{
                        avatar: display.avatar,
                        displayName: display.name,
                      }}
                    />
                  )
                }
              >
                {display.name}
              </SearchResultItem>
            )
          }}
        />
      </FormGroup>
    )
  },
)

/**
 * Gets display props for a provider.
 */
function getDisplayPropsForProvider(
  provider: RequestedProviderSelection,
  nullSelectionPlaceholder: string,
) {
  if (provider === RequestedProviderSelection.firstAvailable) {
    return {
      name: nullSelectionPlaceholder,
      avatar: null,
    }
  }

  return {
    name: provider.displayName,
    avatar: provider.avatar,
  }
}
